<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { VIEW_HISTORY_ON } from '@vp/render';
import { t } from '../i18n';
import { moduleShortcuts } from '../modules';

/** Keyboard shortcuts reference (opened with "?"). */
const emit = defineEmits<{ close: [] }>();
const box = ref<HTMLElement | null>(null);
onMounted(() => box.value?.focus());

const platformGroups: { title: string; items: [string, string][] }[] = [
  {
    title: 'keys.tools',
    items: [
      ['V', 'tools.select'],
      ['H / Space', 'tools.hand'],
      ['Q', 'tools.lasso'],
      ['R', 'tools.rect'],
      ['O', 'tools.ellipse'],
      ['T', 'tools.text'],
      ['S', 'tools.sticker'],
      ['F', 'tools.frame'],
      ['L', 'tools.line'],
      ['C', 'tools.connector'],
      ['P', 'tools.path'],
      ['B', 'tools.pen'],
      ['E', 'tools.eraser'],
    ],
  },
  {
    title: 'keys.edit',
    items: [
      ['Ctrl+Z', 'cmd.undo'],
      ['Ctrl+Shift+Z / Ctrl+Y', 'cmd.redo'],
      ['Ctrl+C / X / V', 'keys.clipboard'],
      ['Ctrl+D', 'cmd.duplicate'],
      ['Delete', 'cmd.delete'],
      ['Ctrl+A', 'cmd.selectAll'],
      ['Ctrl+G / Ctrl+Shift+G', 'keys.group'],
      ['Ctrl+S', 'keys.save'],
      ['Tab / Shift+Tab', 'keys.next'],
      ['Ctrl+Alt+U / S / I / E', 'keys.shapeOps'],
      ['Ctrl+L', 'keys.lock'],
      ['Ctrl+Alt+C / V', 'keys.style'],
      ['Ctrl+Alt+M', 'keys.notes'],
      ['Ctrl+Alt+K', 'components.make'],
      ['Ctrl+] / Ctrl+[', 'keys.order'],
      ['Ctrl+Shift+] / [', 'keys.orderEdge'],
      ['← ↑ → ↓ (Shift)', 'keys.nudge'],
      ['Ctrl+Alt+← ↑ → ↓', 'flow.addConnected'],
      ['Enter', 'cmd.editText'],
      ['Esc', 'keys.escape'],
    ],
  },
  {
    title: 'keys.view',
    items: [
      ['Ctrl+K', 'keys.palette'],
      ['Ctrl+F', 'search.title'],
      ['Ctrl + = / −', 'keys.zoom'],
      ['Ctrl+0', 'cmd.zoomReset'],
      ['Shift+1', 'cmd.fitAll'],
      ['Shift+2', 'keys.fitSelection'],
      ['Z', 'keys.zen'],
      // OFF: previous/next view (`VIEW_HISTORY_ON`)
      ...(VIEW_HISTORY_ON ? ([['Alt+←', 'keys.viewBack'], ['Alt+→', 'keys.viewForward']] as [string, string][]) : []),
      ['?', 'keys.title'],
    ],
  },
  {
    title: 'keys.mouse',
    items: [
      ['#keys.shiftClick', 'keys.addSelect'],
      ['Shift', 'keys.keepRatio'],
      ['Alt', 'keys.noSnap'],
      ['#keys.ctrlDrag', 'keys.dragCopy'],
      ['#keys.doubleClick', 'keys.dblclick'],
    ],
  },
];

/** Module shortcuts (API 2.2) come after the platform's; their labels are already localized. */
const groups = computed(() => {
  const mods = moduleShortcuts();
  return mods.length ? [...platformGroups, { title: 'keys.modules', items: mods.map((m): [string, string] => [m.keys, '=' + m.label]) }] : platformGroups;
});

function label(k: string): string {
  if (k.startsWith('=')) return k.slice(1);
  return t(k).replace(/\s*\([^)]*\)$/, '');
}
</script>

<template>
  <div class="dialog-backdrop" @click.self="emit('close')" @keydown.esc="emit('close')">
    <div ref="box" class="dialog keys" role="dialog" aria-modal="true" :aria-label="t('keys.title')" tabindex="-1">
      <h3>{{ t('keys.title') }}</h3>
      <div class="cols">
        <section v-for="g in groups" :key="g.title">
          <p class="group-title">{{ t(g.title) }}</p>
          <dl>
            <template v-for="[k, l] in g.items" :key="k + l">
              <dt>{{ label(l) }}</dt>
              <dd><kbd>{{ k.startsWith('#') ? t(k.slice(1)) : k }}</kbd></dd>
            </template>
          </dl>
        </section>
      </div>
      <div class="actions">
        <button class="pill" @click="emit('close')">{{ t('common.close') }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.keys {
  width: min(820px, calc(100vw - 32px));
  border-radius: 20px;
  outline: none;
}
.cols {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 8px 28px;
}
dl {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 12px;
  margin: 0;
  font-size: 12.5px;
}
dt {
  color: var(--text-2);
}
dd {
  margin: 0;
  text-align: right;
}
kbd {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--hover);
  border: 1px solid var(--border);
  font: 11.5px ui-monospace, Consolas, monospace;
  color: var(--text);
  white-space: nowrap;
}
</style>
